import { api } from "./api.js";

/**
 * The iOS and Android apps (packages/mobile).
 *
 * Like the desktop app, each is a native window onto this same site: same
 * origin, same cookie. What the phone shell adds is what a mobile browser
 * cannot do well — native push that arrives with the app closed, and the
 * Android back button. Capacitor injects `window.Capacitor` into the pages it
 * loads, which is how the site knows it is inside the app.
 *
 * The Capacitor plugins are imported lazily, only inside the app, so the site
 * that browsers load does not carry them.
 */

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
};

declare global {
  interface Window {
    Capacitor?: CapacitorGlobal;
  }
}

export type NativePlatform = "ios" | "android";

/** Read once at load: it cannot change during a session. */
export const nativePlatform: NativePlatform | null = (() => {
  if (typeof window === "undefined" || window.Capacitor?.isNativePlatform?.() !== true) return null;
  const platform = window.Capacitor.getPlatform?.();
  return platform === "ios" || platform === "android" ? platform : null;
})();

export const isNativeApp = nativePlatform !== null;

/** Where the push token for this install is kept, so sign-out can unregister it. */
const TOKEN_KEY = "astrum.pushDeviceToken";

function rememberToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage blocked: sign-out then cannot unregister, and the token is
    // cleaned up the next time someone signs in on this phone instead.
  }
}

function storedToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Whether this Android build was made with a Firebase config. Without one the
 * push plugin's register() crashes the app, so it is never called.
 */
async function androidPushAvailable(): Promise<boolean> {
  const { registerPlugin } = await import("@capacitor/core");
  const native = registerPlugin<{ pushAvailable(): Promise<{ available: boolean }> }>("AstrumNative");
  return native
    .pushAvailable()
    .then(({ available }) => available)
    .catch(() => false);
}

export type NativePushPermission = "granted" | "denied" | "prompt";

/** Whether this phone lets the app show notifications, without asking. */
export async function nativePushPermission(): Promise<NativePushPermission | null> {
  if (!isNativeApp) return null;
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const { receive } = await PushNotifications.checkPermissions();
  return receive === "granted" ? "granted" : receive === "denied" ? "denied" : "prompt";
}

/**
 * Asks for permission if the phone has not been asked yet, and registers when
 * it is granted; the token then arrives through the `registration` listener
 * that startNativePush installs. Returns where permission ended up.
 */
export async function enableNativePush(): Promise<NativePushPermission | null> {
  if (!isNativeApp) return null;
  if (nativePlatform === "android" && !(await androidPushAvailable())) return null;
  const { PushNotifications } = await import("@capacitor/push-notifications");
  let { receive } = await PushNotifications.checkPermissions();
  if (receive === "prompt" || receive === "prompt-with-rationale") {
    ({ receive } = await PushNotifications.requestPermissions());
  }
  if (receive === "granted") {
    // The channel the server's pushes name. Android files a push for a
    // channel that does not exist under a generic one, silent by default on
    // some phones. Creating it again is harmless and keeps any changes the
    // person made to it in Settings.
    if (nativePlatform === "android") {
      await PushNotifications.createChannel({ id: "messages", name: "Messages", importance: 4, visibility: 0 });
    }
    await PushNotifications.register();
  }
  return receive === "granted" ? "granted" : receive === "denied" ? "denied" : "prompt";
}

/**
 * Registers this phone for push and routes taps on notifications.
 *
 * Called once the person is signed in. Unlike in a browser, asking for
 * permission straight away is expected here: notifications are why someone
 * installs a chat app, and both platforms show the prompt only once — after
 * that this just re-registers, which keeps the server's token current when
 * the OS rotates it.
 *
 * Returns a cleanup that removes the listeners.
 */
export function startNativePush(navigate: (to: string) => void): () => void {
  if (!nativePlatform) return () => undefined;
  const platform = nativePlatform;
  let cancelled = false;
  const handles: { remove: () => Promise<void> }[] = [];

  void (async () => {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    if (cancelled) return;

    handles.push(
      await PushNotifications.addListener("registration", ({ value }) => {
        rememberToken(value);
        void api.post("/push/devices", { platform, token: value }).catch(() => undefined);
      }),
      // A tap on a notification, including the one that launched the app.
      await PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
        const url = (notification.data as { url?: unknown } | undefined)?.url;
        // Only in-app paths: the payload is ours, but a destination is never
        // allowed to send the app off to another site.
        if (typeof url === "string" && url.startsWith("/") && !url.startsWith("//")) navigate(url);
      }),
    );
    if (!cancelled) await enableNativePush();
  })().catch(() => undefined);

  return () => {
    cancelled = true;
    for (const handle of handles) void handle.remove();
  };
}

/**
 * Stops this phone receiving pushes for the person signing out.
 *
 * Must run before the session ends, since the server only removes a token for
 * the viewer it belongs to.
 */
export async function unregisterNativePush(): Promise<void> {
  const token = storedToken();
  if (!isNativeApp || !token) return;
  rememberToken(null);
  await api.post("/push/devices/remove", { token }).catch(() => undefined);
}

/**
 * The Android back button: go back in the app's history, and leave the app
 * (to the home screen, not closed) when there is nowhere left to go back to.
 * Without this, back closes the app from any screen.
 */
export function startNativeBackButton(): void {
  if (nativePlatform !== "android") return;
  void import("@capacitor/app")
    .then(({ App }) =>
      App.addListener("backButton", ({ canGoBack }) => {
        if (canGoBack) window.history.back();
        else void App.minimizeApp();
      }),
    )
    .catch(() => undefined);
}

/**
 * Inside the app the page should not zoom. iOS zooms in when a field takes
 * focus with the keyboard up (the sign-in email field is focused on load) and
 * leaves the page zoomed; pinning the scale stops that, and pinch-zoom has no
 * place in an app's screens anyway. The website keeps normal zoom.
 */
export function lockNativeZoom(): void {
  if (!isNativeApp) return;
  const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (viewport && !/maximum-scale/.test(viewport.content)) {
    viewport.content += ", maximum-scale=1, user-scalable=no";
  }
}

/**
 * Marks the page while the on-screen keyboard is up (`keyboard-open` on
 * <html>), so the bottom tab bar can step aside rather than sit on top of the
 * keyboard, as it does in a native app.
 */
export function startNativeKeyboard(): void {
  if (!isNativeApp) return;
  void import("@capacitor/keyboard")
    .then(({ Keyboard }) => {
      const root = document.documentElement;
      void Keyboard.addListener("keyboardWillShow", () => root.classList.add("keyboard-open"));
      void Keyboard.addListener("keyboardWillHide", () => root.classList.remove("keyboard-open"));
    })
    .catch(() => undefined);
}
